'use client';

import dynamic from 'next/dynamic';
import { useEffect, useId, useState } from 'react';
import { FabButton } from '@/app/_components/core/fab-button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/app/_components/core/popover';
import { useLabel } from '@/app/_contexts/labels-context';
import { useMediaQuery } from '@/app/_hooks/use-media-query';
import { cn } from '@/app/_lib/utils';
import { FabPanelContent } from './components/fab-panel-content';
import { shouldRenderFab, shouldRenderFabHeaderCta } from './fab.utils';
import type { FabComponentProps } from './fab.types';

// Mobile-only Drawer variant is loaded lazily via next/dynamic so the vaul
// dependency (~30 KB) stays out of the desktop bundle entirely and only
// reaches mobile clients when the FAB is actually rendered as a Drawer.
const FabDrawerVariant = dynamic(
  () => import('./components/fab-drawer-variant'),
  { ssr: false },
);

// =============================================================================
// Class names — split into Tailwind defaults and design-token classes
// =============================================================================

// Desktop popover panel
const getDefaultPopoverClasses = () =>
  'w-96 overflow-y-auto border border-border bg-popover';
const getTokenPopoverClasses = () =>
  'max-h-fab-panel-max-h-desktop rounded-fab-panel shadow-fab-panel-desktop';

// Mobile drawer panel
const getDefaultDrawerClasses = () => 'border-border bg-popover';
const getTokenDrawerClasses = () =>
  'max-h-fab-panel-max-h-mobile rounded-tl-fab-drawer rounded-tr-fab-drawer pb-fab-safe-bottom shadow-fab-panel-mobile';

// Backdrop overlay — fades in/out around isOpen
const getDefaultBackdropClasses = (isOpen: boolean) =>
  cn(
    'fixed inset-0 z-40 bg-overlay-scrim transition-opacity duration-300 ease-out',
    isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
  );
const getTokenBackdropClasses = () => 'backdrop-blur-fab';

// Mobile sticky CTA bar (SPARKS-122) — flush to bottom edge, white BG, top border, no radius.
// Slides up after first viewport is scrolled past.
// z-index swap (per Valerii): when drawer is closed, bar floats above content (z-100);
// when drawer is open, bar slips below the backdrop+drawer (z-30) so the drawer covers it.
const getMobileBarClasses = (isVisible: boolean, isDrawerOpen: boolean) =>
  cn(
    'fixed inset-x-0 bottom-0  flex items-center gap-2',
    isDrawerOpen ? 'z-30' : 'z-fab',
    'border-t border-border bg-popover px-4 py-3 pb-fab-safe-bottom',
    'transition-[transform,opacity] duration-300 ease-fab-drawer will-change-transform motion-reduce:transition-none',
    isVisible
      ? 'translate-y-0 opacity-100'
      : 'pointer-events-none translate-y-full opacity-0'
  );

export default function FabComponent({
  fabLayout,
  fabHeadline,
  fabSubline,
  fabCopytext,
  fabFooter,
  fabCtas,
  headerCtaPrimary,
  headerCtaSecondary,
  headerCtaPrimaryState,
}: FabComponentProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPastFirstViewport, setIsPastFirstViewport] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 48rem)');
  const panelTitleId = useId();

  const openLabel = useLabel('fabOpen', 'Kontakt öffnen');
  const closeLabel = useLabel('fabClose', 'Kontakt schließen');

  const willRender = shouldRenderFab({ fabLayout, fabCtas });
  const showStickyBar =
    !isDesktop &&
    shouldRenderFabHeaderCta({ headerCtaPrimary, headerCtaPrimaryState });

  // Sticky-bar visibility (SPARKS-122): only after first viewport scrolled past
  useEffect(() => {
    if (!willRender || !showStickyBar) return;
    const onScroll = () => {
      setIsPastFirstViewport(window.scrollY >= window.innerHeight);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [willRender, showStickyBar]);

  // Mobile Drawer uses modal={false}, which disables vaul's built-in Escape
  // handling. Wire it up manually so AT users can dismiss the panel.
  useEffect(() => {
    if (!isOpen || isDesktop) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => { document.removeEventListener('keydown', onKeyDown); };
  }, [isOpen, isDesktop]);

  if (!willRender) return null;

  const content = (
    <FabPanelContent
      headline={fabHeadline}
      subline={fabSubline}
      copytext={fabCopytext}
      ctas={fabCtas}
      footerNote={fabFooter}
      onItemClick={() => { setIsOpen(false); }}
      panelTitleId={panelTitleId}
    />
  );

  // -- Desktop (≥ md): Popover anchored above floating circle FAB --
  if (isDesktop) {
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <FabButton
            isOpen={isOpen}
            label={isOpen ? closeLabel : openLabel}
            variant="default"
            shape="circle"
            data-track-id="fab-toggle"
          />
        </PopoverTrigger>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- aria-hidden pointer-only backdrop: a click-to-dismiss convenience with no keyboard semantics. Keyboard users close via Escape (handled by Radix Popover). */}
        <div
          className={cn(getDefaultBackdropClasses(isOpen), getTokenBackdropClasses())}
          onClick={() => { setIsOpen(false); }}
          aria-hidden={!isOpen}
        />
        <PopoverContent
          side="top"
          align="end"
          sideOffset={20}
          aria-labelledby={fabHeadline ? panelTitleId : undefined}
          aria-label={!fabHeadline ? 'Kontakt' : undefined}
          className={cn(getDefaultPopoverClasses(), getTokenPopoverClasses())}
        >
          {content}
        </PopoverContent>
      </Popover>
    );
  }

  // -- Mobile (< md): Drawer variant rendered from a lazy-loaded module so
  // vaul never enters the desktop bundle and ships to mobile only on demand.
  return (
    <FabDrawerVariant
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      showStickyBar={showStickyBar}
      isPastFirstViewport={isPastFirstViewport}
      headerCtaPrimary={headerCtaPrimary ?? undefined}
      headerCtaSecondary={headerCtaSecondary ?? undefined}
      openLabel={openLabel}
      closeLabel={closeLabel}
      fabHeadline={fabHeadline ?? undefined}
      panelTitleId={panelTitleId}
      defaultBackdropClasses={getDefaultBackdropClasses(isOpen)}
      tokenBackdropClasses={getTokenBackdropClasses()}
      defaultDrawerClasses={getDefaultDrawerClasses()}
      tokenDrawerClasses={getTokenDrawerClasses()}
      mobileBarClasses={getMobileBarClasses(isPastFirstViewport, isOpen)}
      content={content}
      onBackdropClick={() => { setIsOpen(false); }}
    />
  );
}

export { FabComponent };
